/***********************************
 *  Grade2MathLesson.js
 ***********************************/
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./Grade2MathLesson.css";
import PlaceValueAdditionDemo from "../MathTools/PlaceValueAdditionDemo.js";
import MultiplicationDemo from "../MathTools/MultiplicationDemo.js";
import ProgressBar from "../MathPagesStructure/ProgressBar.js";
import LessonUI from "../MathPagesStructure/LessonUI.js";
import { StoryModeQuiz, QuizResultScreen, FinalQuizUI } from "../MathPagesStructure/StoryModeStructure.js";
import { mathChapterPages } from "../MathLessonPools";
import { generateChapterQuiz, generateFinalQuiz} from "./Grade2QuizGenerator.js";
import { auth, db } from "../../firebase";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";

// Example shapes
const shapeNames = ["Triangle", "Square", "Circle", "Pentagon", "Hexagon"];


/** -------------- MAIN COMPONENT -------------- **/

export default function Grade2MathLesson({ onAwardTokens} ) {
  // 1) Basic states
  const [currentChapter, setCurrentChapter] = useState(1);
  const [chaptersFinished, setChaptersFinished] = useState(false);

  // 2) Chapter Quiz states
  const [chapterQuiz, setChapterQuiz] = useState([]);
  const [chapQuizIndex, setChapQuizIndex] = useState(0);
  const [chapQuizScore, setChapQuizScore] = useState(0);
  const [chapQuizFinished, setChapQuizFinished] = useState(false);

  // 3) Page navigation
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  // 4) Final Quiz
  const [showFinalQuiz, setShowFinalQuiz] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [finalIndex, setFinalIndex] = useState(0);
  const [finalScore, setFinalScore] = useState(0);
  const [finalFinished, setFinalFinished] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);

  // 5) LocalStorage completion
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);

  // 6) Logged in user
  const [user, setUser] = useState(null);

  // 7) For awarding tokens only once
  const hasAwardedTokens = useRef(false);

  // 8) States for demos (Place Value, Multiplication, Shapes)
  const [tens, setTens] = useState(3);
  const [ones, setOnes] = useState(5);
  const [tensToAdd, setTensToAdd] = useState(2);
  const [onesToAdd, setOnesToAdd] = useState(4);
  const [resultTens, setResultTens] = useState(3);
  const [resultOnes, setResultOnes] = useState(5);

  const [multiplicationNum1, setMultiplicationNum1] = useState(2);
  const [multiplicationNum2, setMultiplicationNum2] = useState(3);
  const [multiplicationResult, setMultiplicationResult] = useState(null);

  const [shapeIndex, setShapeIndex] = useState(0);
  const canvasRef = useRef(null);

  /** -------------- Load from LocalStorage on Mount -------------- **/
  useEffect(() => {
    const done = localStorage.getItem("grade2MathComplete");
    if (done === "true") {
      setHasCompletedBefore(true);
      setChaptersFinished(true);
    }
    const logged = JSON.parse(localStorage.getItem("loggedInUser"));
    if (logged) setUser(logged);
  }, []);

  /** -------------- If chapters finish, store to localStorage -------------- **/
  useEffect(() => {
    if (chaptersFinished) {
      localStorage.setItem("grade2MathComplete", "true");
    }
  }, [chaptersFinished]);

  /** -------------- Final Quiz Timer -------------- **/
useEffect(() => {
  let alive = true;
  if (showFinalQuiz && !finalFinished && timeLeft > 0) {
    const t = setTimeout(() => {
      if (alive) setTimeLeft(s => s - 1);
    }, 1000);
    return () => { alive = false; clearTimeout(t); };
  } else if (timeLeft === 0) {
    setFinalFinished(true);
  }
}, [showFinalQuiz, finalFinished, timeLeft]);

  /** -------------- LESSON PAGES -------------- **/
  const chapterPages = mathChapterPages[2]; // Use for current grade

  // -------------- CHAPTER TOPICS --------------
  const chapterTopics = {
    1: "numberConcepts",
    2: "patternsAlgebra",
    3: "measurementGeometry",
    4: "dataProbability",
    5: "financialLiteracy"
  };

  // ---- Jump to a chosen chapter (for the "hasCompletedBefore" UI)
  function jumpToChapter(chNum) {
    setCurrentChapter(chNum);
    // find first page index that belongs to that chapter
    const idx = chapterPages.findIndex(p => p.chapter === chNum);
    if (idx >= 0) {
      setCurrentPageIndex(idx);
    }
    // reset quiz states, final quiz
    setChapterQuiz([]);
    setChapQuizIndex(0);
    setChapQuizScore(0);
    setChapQuizFinished(false);
    setShowFinalQuiz(false);
  }

  // ---- Jump directly to final quiz
  function jumpDirectlyToFinal() {
    startFinalQuiz();
  }

  /** -------------- CHAPTER QUIZ HANDLERS -------------- **/
  function startChapterQuiz() {
    const topic = chapterTopics[currentChapter];
    const quiz = generateChapterQuiz(topic, 10);
    setChapterQuiz(quiz);
    setChapQuizIndex(0);
    setChapQuizScore(0);
    setChapQuizFinished(false);
  }

  function handleChapterAnswer(choice) {
    const cQ = chapterQuiz[chapQuizIndex];
    if (choice === cQ.correctAnswer) {
      setChapQuizScore(s => s + 1);
    }
    if (chapQuizIndex < chapterQuiz.length - 1) {
      setChapQuizIndex(chapQuizIndex + 1);
    } else {
      setChapQuizFinished(true);
    }
  }

  function handleChapterQuizFinish() {
    if (chapQuizScore >= 5) {
      // user passes => move to next chapter or finish
      if (currentChapter < 5) {
        setCurrentChapter(currentChapter + 1);
        // jump to next chapter's first page
        const nextIdx = chapterPages.findIndex(p => p.chapter === (currentChapter + 1));
        if (nextIdx >= 0) {
          setCurrentPageIndex(nextIdx);
        }
      } else {
        // all chapters done
        setChaptersFinished(true);
      }
    } else {
      alert("You scored below 50%. Please review or retake!");
      // Let them retake if you want:
      setChapQuizIndex(0);
      setChapQuizScore(0);
      setChapQuizFinished(false);
    }

    // reset the quiz so it doesn't show
    setChapterQuiz([]);
    setChapQuizIndex(0);
    setChapQuizScore(0);
    setChapQuizFinished(false);
  }

  /** -------------- Place Value & Multiplication Demos -------------- **/
  function handleAddition() {
    let newOnes = ones + onesToAdd;
    let newTens = tens + tensToAdd;
    if (newOnes >= 10) {
      newTens += Math.floor(newOnes / 10);
      newOnes = newOnes % 10;
    }
    setResultTens(newTens);
    setResultOnes(newOnes);
  }

  function handleMultiplication() {
    setMultiplicationResult([]);
    let arr = [];
    for (let i = 1; i <= multiplicationNum2; i++) {
      setTimeout(() => {
        arr.push([...Array(multiplicationNum1).fill("💙")]);
        setMultiplicationResult([...arr]);
      }, i * 300);
    }
  }

  function drawShape() {
    setShapeIndex(prev => {
      const newIdx = (prev + 1) % shapeNames.length;
      const canvas = canvasRef.current;
      if (!canvas) return newIdx;

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#007bff";
      ctx.lineWidth = 3;
      ctx.beginPath();
      switch (newIdx) {
        case 0: // Triangle
          ctx.moveTo(100, 20);
          ctx.lineTo(50, 100);
          ctx.lineTo(150, 100);
          ctx.closePath();
          break;
        case 1: // Square
          ctx.rect(60, 40, 80, 80);
          break;
        case 2: // Circle
          ctx.arc(100, 70, 40, 0, Math.PI * 2);
          break;
        case 3: // Pentagon
          ctx.moveTo(100, 20);
          ctx.lineTo(50, 60);
          ctx.lineTo(70, 110);
          ctx.lineTo(130, 110);
          ctx.lineTo(150, 60);
          ctx.closePath();
          break;
        default: // Hexagon
          ctx.moveTo(100, 20);
          ctx.lineTo(60, 50);
          ctx.lineTo(60, 90);
          ctx.lineTo(100, 120);
          ctx.lineTo(140, 90);
          ctx.lineTo(140, 50);
          ctx.closePath();
          break;
      }
      ctx.stroke();
      return newIdx;
    });
  }

  /** -------------- FINAL QUIZ -------------- **/
  function startFinalQuiz() {
    setFinalQs(generateFinalQuiz(50));
    setFinalIndex(0);
    setFinalScore(0);
    setFinalFinished(false);
    setTimeLeft(300);
    setShowFinalQuiz(true);
  }

  function handleFinalAnswer(choice) {
    const cQ = finalQs[finalIndex];
    if (choice === cQ.correctAnswer) {
      setFinalScore(s => s + 1);
    }
    if (finalIndex < finalQs.length - 1) {
      setFinalIndex(finalIndex + 1);
    } else {
      setFinalFinished(true);
    }
  }

  function handleReturnFromFinal() {
    setShowFinalQuiz(false);
  }

  /** Award token if final quiz ≥ 80% **/
useEffect(() => {
  if (!finalFinished || hasAwardedTokens.current) return;

  (async () => {
    const passed = (finalScore / finalQs.length) * 100 >= 80;
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    try {
      await runTransaction(db, async tx => {
        const userRef = doc(db, "users", uid);
        const snap     = await tx.get(userRef);
        const data     = snap.data() || {};

        const progress = {
          ...(data.progress || {}),
          math_g2: {
            completed:      true,
            finalQuizScore: finalScore,
            finalQuizTotal: finalQs.length,
            lastCompleted:  serverTimestamp(),
          },
        };

        const update = { progress };
        if (passed) update.coins = (data.coins ?? 0) + 1;
        tx.set(userRef, update, { merge: true });
      });

      if (passed && onAwardTokens) onAwardTokens(1);
      hasAwardedTokens.current = true;
    } catch (err) {
      console.error("Save math_g2 progress failed →", err);
    }
  })();
}, [finalFinished, finalScore, finalQs.length, onAwardTokens]);

  /** -------------- RENDERING -------------- **/

  // e.g. 0% at ch1, 20% at ch2, etc. or do total pages if you prefer
  const progress = chaptersFinished ? 100 : Math.floor(((currentChapter - 1) / 5) * 100);

  /** -------------- JSX RETURN -------------- **/
  return (
    <div className="grade2-math-page">
      {/* PROGRESS BAR */}
      <ProgressBar progress={progress} />
  
      {/* If user finished before => jump to any chapter or final */}
      {hasCompletedBefore && !showFinalQuiz && !chapterQuiz.length && !chapQuizFinished && (
        <div style={{ margin: "15px 0", textAlign: "center", color: "#555" }}>
          <p>You previously completed all Grade 2 lessons.</p>
          <p>Jump to any chapter or retake the final quiz for practice.</p>
          <div>
            {[1, 2, 3, 4, 5].map(ch => (
              <button
                key={ch}
                style={{ marginRight: 8 }}
                onClick={() => jumpToChapter(ch)}
              >
                Go to Chapter {ch}
              </button>
            ))}
            <button onClick={jumpDirectlyToFinal}>Go to Final Quiz</button>
          </div>
        </div>
      )}
  
      {/* If not done with chapters => show current chapter's pages & quiz */}
      {!chaptersFinished && (
  <>
    {/* If quiz not started, show lesson pages */}
    {chapterQuiz.length === 0 && (
      <LessonUI
      pages={chapterPages.filter(p => p.chapter === currentChapter)}
      currentPageIndex={currentPageIndex - chapterPages.findIndex(p => p.chapter === currentChapter)}
      onPrevPage={() => setCurrentPageIndex(i => i - 1)}
      onNextPage={() => setCurrentPageIndex(i => i + 1)}
      onStartQuiz={startChapterQuiz}
      backTo="/math"
      renderExtraSection={({ page }) => {
        if (page.demoType === "placeValueAddition") {
          return (
            <PlaceValueAdditionDemo
              tens={tens}
              ones={ones}
              tensToAdd={tensToAdd}
              onesToAdd={onesToAdd}
              resultTens={resultTens}
              resultOnes={resultOnes}
              setTens={setTens}
              setOnes={setOnes}
              setTensToAdd={setTensToAdd}
              setOnesToAdd={setOnesToAdd}
              handleAddition={handleAddition}
            />
          );
        }
        if (page.demoType === "multiplication") {
          return (
            <MultiplicationDemo
              multiplicationNum1={multiplicationNum1}
              multiplicationNum2={multiplicationNum2}
              multiplicationResult={multiplicationResult}
              setMultiplicationNum1={setMultiplicationNum1}
              setMultiplicationNum2={setMultiplicationNum2}
              handleMultiplication={handleMultiplication}
            />
          );
        }
        if (page.demoType === "shapes") {
          return (
            <div>
              <canvas ref={canvasRef} width="200" height="120" className="shapes-canvas" />
              <p className="shape-name">{shapeNames[shapeIndex]}</p>
              <button onClick={drawShape}>Draw Next Shape</button>
            </div>
          );
        }
        return null;
      }}
    />    
    )}

    {/* If quiz is active, show quiz UI */}
    {chapterQuiz.length > 0 && !chapQuizFinished && (
      <StoryModeQuiz
        questions={chapterQuiz}
        onAnswer={handleChapterAnswer}
        currentIndex={chapQuizIndex}
        score={chapQuizScore}
        user={user}
      />
    )}
    {chapQuizFinished && (
      <QuizResultScreen
        score={chapQuizScore}
        total={chapterQuiz.length}
        onContinue={handleChapterQuizFinish}
      />
    )}
  </>
)}

  
      {/* If chapters done, show final quiz button (if not started) */}
      {chaptersFinished && !showFinalQuiz && (
        <div style={{ textAlign: "center" }}>
          <h3>All Chapters Completed!</h3>
          <p>You're at 100%. Take the Final Quiz:</p>
          <button onClick={startFinalQuiz}>Start Final Quiz</button>
        </div>
      )}
  
      {/* Final quiz if started */}
      {showFinalQuiz && (
        <FinalQuizUI
          questions={finalQs}
          currentIndex={finalIndex}
          score={finalScore}
          timeLeft={timeLeft}
          onAnswer={handleFinalAnswer}
          quizFinished={finalFinished}
          onReturn={handleReturnFromFinal}
          resultMsg={user && (
            <p>
              You now have <strong>{user.tokens ?? 0}</strong> tokens!
            </p>
          )}
        />
      )}
  
      <br />
      <Link to="/math" className="grade2-math-back">
        ← Back to Math Page
      </Link>
    </div>
  );  
}
